import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Card, Row, Col, Statistic, Input, Button, Tag, Space, Tooltip,
  Modal, message, Popconfirm, Typography, Empty, Spin, Badge, DatePicker
} from "antd";
import {
  SafetyCertificateOutlined,
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  MailOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  UserOutlined,
  BookOutlined,
  CalendarOutlined,
  TrophyOutlined,
  RiseOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  ClearOutlined
} from "@ant-design/icons";
import axios from "axios";
import API_BASE from "../../config/apiConfig";

const { Title, Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

// 🎨 Couleurs Nomidya
const NOMIDYA_BLUE = "#0054A6";
const NOMIDYA_ORANGE = "#E8860C";

const SentCertificatesManager = () => {
  // ============================================
  // STATES
  // ============================================
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [certificates, setCertificates] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [exporting, setExporting] = useState(false);

  // ============================================
  // HELPERS API
  // ============================================
  const getAuthHeaders = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return {
      Authorization: "Bearer " + (user?.accessToken || ""),
    };
  };

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  useEffect(() => {
    loadAllCertificates();
    loadStats();
  }, []);

  const loadAllCertificates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE}/certificats/sent-certificates/all`,
        { headers: getAuthHeaders() }
      );
      setCertificates(response.data.sentCertificates || []);
    } catch (e) {
      console.error("Erreur chargement certificats:", e);
      message.error("Impossible de charger la liste des certificats.");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(
        `${API_BASE}/certificats/sent-certificates/stats`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (e) {
      console.error("Erreur chargement statistiques:", e);
    } finally {
      setLoadingStats(false);
    }
  };

  // ============================================
  // ACTIONS
  // ============================================
  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(
        `${API_BASE}/certificats/sent-certificate/${id}`,
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        message.success(response.data.message || "✅ Certificat supprimé");
        await loadAllCertificates();
        await loadStats();
      } else {
        message.error(response.data.message || "Erreur lors de la suppression");
      }
    } catch (e) {
      console.error(e);
      message.error("Erreur lors de la suppression.");
    }
  };

  const handleResend = async (record) => {
    try {
      const response = await axios.post(
        `${API_BASE}/certificats/sent-certificate/${record.id}/resend`,
        null,
        {
          headers: getAuthHeaders(),
          params: { targetEmail: record.userEmail },
        }
      );
      if (response.data.success) {
        message.success(response.data.message || "✅ Email renvoyé");
      } else {
        message.error(response.data.message || "Échec du renvoi");
      }
    } catch (e) {
      console.error(e);
      message.error("Erreur lors du renvoi de l'email.");
    }
  };

  const handleView = (record) => {
    const url =
      record.verificationUrl ||
      `https://nomidya.com/certificate/verify/${record.verificationCode}`;
    window.open(url, "_blank");
  };

  // ============================================
  // EXPORT CSV
  // ============================================
  const handleExportCSV = () => {
    setExporting(true);
    try {
      const headers = [
        "ID",
        "Étudiant",
        "Email",
        "Cours/Formation",
        "Code de vérification",
        "Date d'envoi",
      ];

      const rows = filteredCertificates.map((c) => [
        c.id,
        `"${(c.userName || "").replace(/"/g, '""')}"`,
        c.userEmail || "",
        `"${(c.courseName || "").replace(/"/g, '""')}"`,
        c.verificationCode || "",
        c.dateEnvoi ? new Date(c.dateEnvoi).toLocaleString("fr-FR") : "",
      ]);

      const csvContent =
        "\uFEFF" + // BOM pour Excel UTF-8
        [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `certificats_envoyes_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`✅ ${filteredCertificates.length} certificat(s) exporté(s) en CSV`);
    } catch (e) {
      console.error(e);
      message.error("Erreur lors de l'export CSV");
    } finally {
      setExporting(false);
    }
  };

  // ============================================
  // EXPORT EXCEL (XLSX)
  // ============================================
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Import dynamique de xlsx (déjà dans tes dépendances)
      const XLSX = await import("xlsx");

      const data = filteredCertificates.map((c) => ({
        ID: c.id,
        Étudiant: c.userName || "",
        Email: c.userEmail || "",
        "Cours/Formation": c.courseName || "",
        "Code de vérification": c.verificationCode || "",
        "Date d'envoi": c.dateEnvoi
          ? new Date(c.dateEnvoi).toLocaleString("fr-FR")
          : "",
        "URL de vérification": c.verificationUrl || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      // Largeurs des colonnes
      worksheet["!cols"] = [
        { wch: 6 },   // ID
        { wch: 30 },  // Étudiant
        { wch: 30 },  // Email
        { wch: 45 },  // Cours
        { wch: 25 },  // Code
        { wch: 20 },  // Date
        { wch: 50 },  // URL
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Certificats");

      XLSX.writeFile(
        workbook,
        `certificats_envoyes_${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      message.success(`✅ ${filteredCertificates.length} certificat(s) exporté(s) en Excel`);
    } catch (e) {
      console.error(e);
      message.error("Erreur lors de l'export Excel. Vérifie que la lib xlsx est installée.");
    } finally {
      setExporting(false);
    }
  };

  // ============================================
  // FILTRAGE
  // ============================================
  const filteredCertificates = useMemo(() => {
    let result = [...certificates];

    // Filtre texte (nom étudiant, cours, email, code)
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      result = result.filter(
        (c) =>
          (c.userName || "").toLowerCase().includes(q) ||
          (c.courseName || "").toLowerCase().includes(q) ||
          (c.userEmail || "").toLowerCase().includes(q) ||
          (c.verificationCode || "").toLowerCase().includes(q)
      );
    }

    // Filtre date
    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].startOf("day").toDate();
      const end = dateRange[1].endOf("day").toDate();
      result = result.filter((c) => {
        if (!c.dateEnvoi) return false;
        const d = new Date(c.dateEnvoi);
        return d >= start && d <= end;
      });
    }

    return result;
  }, [certificates, searchText, dateRange]);

  // ============================================
  // COLONNES DU TABLEAU
  // ============================================
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      align: "center",
      sorter: (a, b) => a.id - b.id,
      render: (id) => (
        <Tag color="blue" style={{ fontWeight: "bold", margin: 0 }}>
          #{id}
        </Tag>
      ),
    },
    {
      title: (
        <>
          <UserOutlined style={{ marginRight: 6 }} /> Étudiant
        </>
      ),
      dataIndex: "userName",
      key: "userName",
      ellipsis: true,
      sorter: (a, b) => (a.userName || "").localeCompare(b.userName || ""),
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#262626" }}>{text || "—"}</div>
          {record.userEmail && (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              <MailOutlined style={{ marginRight: 4 }} />
              {record.userEmail}
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <>
          <BookOutlined style={{ marginRight: 6 }} /> Cours / Formation
        </>
      ),
      dataIndex: "courseName",
      key: "courseName",
      ellipsis: true,
      sorter: (a, b) => (a.courseName || "").localeCompare(b.courseName || ""),
      render: (text) => (
        <Tag
          color="purple"
          style={{
            margin: 0,
            maxWidth: "100%",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {text || "—"}
        </Tag>
      ),
    },
    {
      title: "Code",
      dataIndex: "verificationCode",
      key: "verificationCode",
      width: 200,
      ellipsis: true,
      render: (code) =>
        code ? (
          <Text code copyable style={{ fontSize: 11 }}>
            {code}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: (
        <>
          <CalendarOutlined style={{ marginRight: 6 }} /> Date d'envoi
        </>
      ),
      dataIndex: "dateEnvoi",
      key: "dateEnvoi",
      width: 170,
      sorter: (a, b) =>
        new Date(a.dateEnvoi || 0) - new Date(b.dateEnvoi || 0),
      defaultSortOrder: "descend",
      render: (date) => {
        if (!date) return <Text type="secondary">—</Text>;
        const d = new Date(date);
        return (
          <div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>
              {d.toLocaleDateString("fr-FR")}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {d.toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir la vérification">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              style={{ borderColor: NOMIDYA_BLUE, color: NOMIDYA_BLUE }}
            />
          </Tooltip>
          <Tooltip title="Renvoyer l'email">
            <Popconfirm
              title="Renvoyer le certificat ?"
              description={`L'email sera renvoyé à : ${record.userEmail || "?"}`}
              okText="Renvoyer"
              cancelText="Annuler"
              onConfirm={() => handleResend(record)}
              disabled={!record.userEmail}
            >
              <Button
                size="small"
                icon={<MailOutlined />}
                disabled={!record.userEmail}
                style={{
                  borderColor: NOMIDYA_ORANGE,
                  color: NOMIDYA_ORANGE,
                }}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Supprimer">
            <Popconfirm
              title="Supprimer ce certificat ?"
              description="L'étudiant pourra de nouveau recevoir un certificat pour ce cours."
              okText="Supprimer"
              okType="danger"
              cancelText="Annuler"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ============================================
  // RENDU
  // ============================================
  return (
    <div style={{ padding: "8px 4px" }}>
      {/* ====== Bandeau titre ====== */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NOMIDYA_BLUE} 0%, #0067cc 100%)`,
          borderRadius: 14,
          padding: "16px 20px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 4px 14px rgba(0,84,166,0.25)",
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: NOMIDYA_ORANGE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SafetyCertificateOutlined style={{ fontSize: 24, color: "#fff" }} />
        </div>
        <div style={{ flex: 1 }}>
          <Title level={5} style={{ color: "#fff", margin: 0 }}>
            Certificats envoyés
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13 }}>
            Consultez, gérez, exportez et supprimez les certificats délivrés.
          </Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={loading || loadingStats}
          onClick={() => {
            loadAllCertificates();
            loadStats();
          }}
          style={{
            background: "rgba(255,255,255,0.15)",
            borderColor: "rgba(255,255,255,0.3)",
            color: "#fff",
          }}
        >
          Actualiser
        </Button>
      </div>

      {/* ====== Statistiques ====== */}
      <Spin spinning={loadingStats}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Card
              size="small"
              bordered={false}
              style={{
                borderLeft: `4px solid ${NOMIDYA_BLUE}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                borderRadius: 10,
              }}
            >
              <Statistic
                title="Total délivrés"
                value={stats?.total || 0}
                prefix={
                  <SafetyCertificateOutlined style={{ color: NOMIDYA_BLUE }} />
                }
                valueStyle={{ color: NOMIDYA_BLUE, fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              size="small"
              bordered={false}
              style={{
                borderLeft: `4px solid ${NOMIDYA_ORANGE}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                borderRadius: 10,
              }}
            >
              <Statistic
                title="Aujourd'hui"
                value={stats?.today || 0}
                prefix={<ClockCircleOutlined style={{ color: NOMIDYA_ORANGE }} />}
                valueStyle={{ color: NOMIDYA_ORANGE, fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              size="small"
              bordered={false}
              style={{
                borderLeft: "4px solid #52c41a",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                borderRadius: 10,
              }}
            >
              <Statistic
                title="Cette semaine"
                value={stats?.thisWeek || 0}
                prefix={<RiseOutlined style={{ color: "#52c41a" }} />}
                valueStyle={{ color: "#52c41a", fontSize: 24 }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card
              size="small"
              bordered={false}
              style={{
                borderLeft: "4px solid #722ed1",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                borderRadius: 10,
              }}
            >
              <Statistic
                title="Ce mois"
                value={stats?.thisMonth || 0}
                prefix={<CalendarOutlined style={{ color: "#722ed1" }} />}
                valueStyle={{ color: "#722ed1", fontSize: 24 }}
              />
            </Card>
          </Col>
        </Row>

        {/* ====== Top 5 cours + Top 5 étudiants ====== */}
        {stats && (stats.topCourses?.length > 0 || stats.topStudents?.length > 0) && (
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <TrophyOutlined style={{ color: NOMIDYA_ORANGE }} />
                    <span style={{ fontSize: 13 }}>
                      Top 5 cours les plus certifiés
                    </span>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: 10,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "10px 14px" }}
              >
                {stats.topCourses?.length > 0 ? (
                  stats.topCourses.map((c, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "5px 0",
                        borderBottom:
                          idx < stats.topCourses.length - 1
                            ? "1px dashed #f0f0f0"
                            : "none",
                      }}
                    >
                      <Text
                        ellipsis
                        style={{ fontSize: 12, flex: 1, marginRight: 8 }}
                      >
                        <Tag
                          color={
                            idx === 0
                              ? "gold"
                              : idx === 1
                              ? "silver"
                              : idx === 2
                              ? "orange"
                              : "default"
                          }
                          style={{ marginRight: 4 }}
                        >
                          #{idx + 1}
                        </Tag>
                        {c.courseName}
                      </Text>
                      <Badge
                        count={c.count}
                        style={{ backgroundColor: NOMIDYA_BLUE }}
                      />
                    </div>
                  ))
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aucune donnée"
                  />
                )}
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <CrownOutlined style={{ color: NOMIDYA_ORANGE }} />
                    <span style={{ fontSize: 13 }}>Top 5 étudiants certifiés</span>
                  </Space>
                }
                bordered={false}
                style={{
                  borderRadius: 10,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: "10px 14px" }}
              >
                {stats.topStudents?.length > 0 ? (
                  stats.topStudents.map((s, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "5px 0",
                        borderBottom:
                          idx < stats.topStudents.length - 1
                            ? "1px dashed #f0f0f0"
                            : "none",
                      }}
                    >
                      <Text
                        ellipsis
                        style={{ fontSize: 12, flex: 1, marginRight: 8 }}
                      >
                        <Tag
                          color={
                            idx === 0
                              ? "gold"
                              : idx === 1
                              ? "silver"
                              : idx === 2
                              ? "orange"
                              : "default"
                          }
                          style={{ marginRight: 4 }}
                        >
                          #{idx + 1}
                        </Tag>
                        {s.userName}
                      </Text>
                      <Badge
                        count={s.count}
                        style={{ backgroundColor: NOMIDYA_ORANGE }}
                      />
                    </div>
                  ))
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Aucune donnée"
                  />
                )}
              </Card>
            </Col>
          </Row>
        )}
      </Spin>

      {/* ====== Toolbar : recherche + actions ====== */}
      <Card
        size="small"
        style={{
          marginBottom: 12,
          borderRadius: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={10}>
            <Input
              prefix={<SearchOutlined style={{ color: "#bbb" }} />}
              placeholder="Rechercher (étudiant, cours, email, code)..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="middle"
            />
          </Col>
          <Col xs={24} md={8}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD/MM/YYYY"
              placeholder={["Date début", "Date fin"]}
              style={{ width: "100%" }}
              size="middle"
            />
          </Col>
          <Col xs={24} md={6}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }} wrap>
              <Tooltip title="Effacer les filtres">
                <Button
                  icon={<ClearOutlined />}
                  size="middle"
                  onClick={() => {
                    setSearchText("");
                    setDateRange(null);
                  }}
                  disabled={!searchText && !dateRange}
                >
                  Reset
                </Button>
              </Tooltip>
              <Tooltip title="Exporter en CSV">
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleExportCSV}
                  loading={exporting}
                  disabled={filteredCertificates.length === 0}
                  size="middle"
                  style={{ borderColor: NOMIDYA_BLUE, color: NOMIDYA_BLUE }}
                >
                  CSV
                </Button>
              </Tooltip>
              <Tooltip title="Exporter en Excel (XLSX)">
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  loading={exporting}
                  disabled={filteredCertificates.length === 0}
                  size="middle"
                  type="primary"
                  style={{
                    background: "#52c41a",
                    borderColor: "#52c41a",
                  }}
                >
                  Excel
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
        {(searchText || dateRange) && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#8c8c8c" }}>
            <Tag color="blue">
              {filteredCertificates.length} résultat(s) sur {certificates.length}
            </Tag>
          </div>
        )}
      </Card>

      {/* ====== Tableau principal ====== */}
      <Card
        bordered={false}
        style={{
          borderRadius: 10,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={columns}
          dataSource={filteredCertificates}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1100 }}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} certificat(s)`,
          }}
          locale={{
            emptyText: loading ? (
              "Chargement..."
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Aucun certificat envoyé"
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default SentCertificatesManager;